import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Housekeeping panel — headline "X / Y dhoma gati" + progress bar.
 * The five statuses displayed as a compact key/value list.
 */
export default function HousekeepingPanel({ overview }) {
    const { t } = useTranslation();
    const clean       = overview?.clean || 0;
    const dirty       = overview?.dirty || 0;
    const maintenance = overview?.maintenance || 0;
    const blocked     = overview?.blocked || 0;
    const oos         = overview?.out_of_order || 0;
    const total       = overview?.total_rooms || (clean + dirty + maintenance + blocked + oos);
    const ready       = clean;
    const pct         = total ? Math.round((ready / total) * 100) : 0;

    const rows = [
        { key: "hk_clean",        value: clean,       dot: "bg-emerald-500" },
        { key: "hk_dirty",        value: dirty,       dot: "bg-amber-500" },
        { key: "hk_maintenance",  value: maintenance, dot: "bg-rose-500" },
        { key: "hk_blocked",      value: blocked,     dot: "bg-slate-400" },
        { key: "hk_out_of_order", value: oos,         dot: "bg-violet-500" },
    ];

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="housekeeping-card">
            <div className="px-5 pt-4">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.housekeeping")}
                </h2>
                <div className="mt-3 flex items-baseline gap-2">
                    <div className="text-[26px] font-semibold leading-none tracking-tight text-foreground tabular-nums">
                        {ready} / {total}
                    </div>
                    <div className="text-[12px] text-muted-foreground">
                        {t("dashboard.rooms_ready_short")}
                    </div>
                </div>
                <div className="mt-3 h-[6px] w-full overflow-hidden rounded-full bg-muted">
                    <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            <ul className="mt-3 px-5">
                {rows.map((row) => (
                    <li
                        key={row.key}
                        className="flex items-center justify-between border-t border-border/60 py-2 text-[13px] first:border-t-0"
                    >
                        <span className="flex items-center gap-2 text-foreground/90">
                            <span className={`inline-flex h-1.5 w-1.5 rounded-full ${row.dot}`} />
                            {t(`dashboard.${row.key}`)}
                        </span>
                        <span className="font-medium tabular-nums text-foreground">{row.value}</span>
                    </li>
                ))}
            </ul>

            <div className="px-5 py-3">
                <Link
                    to="/housekeeping"
                    className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#ff7b02] hover:underline"
                    data-testid="hk-open-link"
                >
                    {t("dashboard.open_cleaning")}
                    <ChevronRight size={13} />
                </Link>
            </div>
        </section>
    );
}
